import { MutableRefObject, PropsWithChildren, useCallback } from 'react';
import { ToolsDrawerSectionContent, ToolsDrawerSectionHeader } from '../ToolsDrawer.styles';
import { Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import { SxProps, useTheme } from '@mui/material';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useTranslation } from 'react-i18next';

export interface IToolsDrawerSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  contentRef?: MutableRefObject<HTMLDivElement | null>;
  onCloseNavItems?: () => void;
  hideCollapseButton?: boolean;
  showOnlyContent?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  contentSx?: SxProps;
}

export const ToolsDrawerSection = (props: PropsWithChildren<IToolsDrawerSectionProps>) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);
  const {
    children,
    title,
    isOpen,
    onToggle,
    onCloseNavItems,
    contentRef,
    disabled = false,
    hideCollapseButton = false,
    showOnlyContent = false,
    sx = {},
    contentSx = {},
  } = props;
  const { palette } = useTheme();

  const showContent = isOpen && !disabled;
  const iconSx = {
    width: 20,
    height: 20,
    color: disabled ? palette.text.disabled : palette.text.secondary,
  };

  const handleToggle = useCallback(() => {
    if (!disabled) onToggle();
  }, [disabled, onToggle]);

  return (
    <Stack height="100%" sx={sx}>
      {!showOnlyContent ? (
        <>
          <ToolsDrawerSectionHeader height={48}>
            <Stack
              onClick={handleToggle}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              height={32}
              px={0.5}
              sx={{
                cursor: disabled ? 'auto' : 'pointer',
                borderRadius: 1.5,
                '&:hover': {
                  backgroundColor: disabled ? 'transparent' : palette.action.hover,
                },
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Tooltip
                  title={
                    disabled
                      ? null
                      : isOpen
                      ? t('tooltips.collapseContents')
                      : t('tooltips.expandContents')
                  }
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle();
                    }}
                    disabled={disabled}
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <KeyboardDoubleArrowUpRoundedIcon
                      sx={{
                        ...iconSx,
                        transform: `rotate(${isOpen && !disabled ? 0 : 180}deg)`,
                        transition: 'transform 0.2s',
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Typography
                  fontWeight={500}
                  color={disabled ? palette.text.disabled : palette.text.secondary}
                  sx={{ userSelect: 'none' }}
                >
                  {title}
                </Typography>
              </Stack>
              {onCloseNavItems && !hideCollapseButton && isOpen && (
                <Tooltip title={t('tooltips.collapseLayers')}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onCloseNavItems();
                    }}
                    disabled={disabled}
                    size="small"
                    sx={{ p: 0 }}
                  >
                    <UnfoldLessRoundedIcon sx={iconSx} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </ToolsDrawerSectionHeader>
          {!disabled && (
            <Collapse in={showContent}>
              <ToolsDrawerSectionContent ref={contentRef} sx={contentSx}>
                {children}
              </ToolsDrawerSectionContent>
            </Collapse>
          )}
        </>
      ) : (
        <ToolsDrawerSectionHeader>{children}</ToolsDrawerSectionHeader>
      )}
    </Stack>
  );
};
